import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons/faExclamationCircle";
import { faTimes } from "@fortawesome/free-solid-svg-icons/faTimes";
import { AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import "./Tools.css";
import ContactForm from "../ContactForm/ContactForm";

const apiUrl =
  window._env_?.VITE_API_BASE_URL !== "${VITE_API_BASE_URL}"
    ? window._env_.VITE_API_BASE_URL
    : "/api";

interface Question {
  id: number;
  question_category: string;
  question: string;
  question_type: string;
  choice: Array<{
    id: number;
    text: string;
    value?: string;
  }>;
}

interface UserResponse {
  id?: number;
  question: number;
  selected_choices: Array<{ id: number }>;
  text_response?: string;
  user?: number;
}

interface RiskProfileResult {
  total_score: number;
  risk_level: number;
  risk_label: string;
}

const Tools: React.FC = () => {
  const questionsData: Question[] = useMemo(
    () => [
      {
        id: 1,
        question_category: "Investment Risk",
        question: "How long do you plan to invest for",
        question_type: "text",
        choice: [],
      },
      {
        id: 2,
        question_category: "Investment Risk",
        question:
          "How would you describe your investment knowledge? Please select the most appropriate option:",
        question_type: "option",
        choice: [
          {
            id: 1,
            text: "Expert: I have a deep understanding of how investments work.",
          },
          {
            id: 2,
            text: "High: I understand the different types of investments and the risks involved.",
          },
          {
            id: 3,
            text: "Moderate: I'm familiar with the basic investment types and their associated risks.",
          },
          {
            id: 4,
            text: "Little: I know a little about investments and their risks.",
          },
          { id: 5, text: "None: I have no knowledge of investments." },
        ],
      },
      {
        id: 3,
        question_category: "Investment Risk",
        question:
          "What is your primary investment objective? Please select the most appropriate option:",
        question_type: "option",
        choice: [
          {
            id: 6,
            text: "To maximise returns, even if it involves high risk.",
          },
          {
            id: 7,
            text: "To achieve high returns, but without taking excessive risk.",
          },
          {
            id: 8,
            text: "To earn solid returns while keeping capital risk to a minimum.",
          },
          {
            id: 9,
            text: "To grow my investment above inflation with minimal risk to my capital.",
          },
          { id: 10, text: "To preserve my capital." },
        ],
      },
      {
        id: 4,
        question_category: "Investment Risk",
        question:
          "How concerned would you be if your investment dropped by 10% over a short period (e.g., 6 months)? Please select the most appropriate option:",
        question_type: "option",
        choice: [
          { id: 11, text: "Not concerned at all." },
          { id: 12, text: "Slightly concerned." },
          { id: 13, text: "Moderately concerned." },
          { id: 14, text: "Very concerned." },
          { id: 15, text: "Extremely concerned." },
        ],
      },
      {
        id: 5,
        question_category: "Investment Risk",
        question:
          "What level of investment risk are you comfortable with? For example: If you invested €10,000 today, what range of possible outcomes after 5 years would be acceptable to you?",
        question_type: "option",
        choice: [
          { id: 16, text: "Between €6,300 and €17,200" },
          { id: 17, text: "Between €6,800 and €16,300" },
          { id: 18, text: "Between €7,900 and €14,000" },
          { id: 19, text: "Between €8,900 and €12,100" },
          { id: 20, text: "Between €9,800 and €11,600" },
        ],
      },
      {
        id: 6,
        question_category: "Investment Risk",
        question:
          "How would you describe your attitude toward taking risk? Please select the most appropriate option:",
        question_type: "option",
        choice: [
          {
            id: 21,
            text: "Willing to take significantly more risk with all my money.",
          },
          {
            id: 22,
            text: "Willing to take a bit more risk with all my money.",
          },
          {
            id: 23,
            text: "Willing to take a bit more risk with part of my money.",
          },
          { id: 24, text: "Unwilling to take any additional risk." },
        ],
      },
      {
        id: 7,
        question_category: "Investment Risk",
        question:
          "Do you have access to other savings in case of a financial emergency?",
        question_type: "option",
        choice: [
          {
            id: 25,
            text: "No, I don't have other savings I could use in an emergency.",
          },
          {
            id: 26,
            text: "I have some savings, but none set aside specifically for emergencies.",
          },
          {
            id: 27,
            text: "Yes, I have savings specifically reserved for emergencies.",
          },
        ],
      },
      {
        id: 8,
        question_category: "Investment Risk",
        question:
          "If this investment were to perform poorly, what impact would it have on your financial situation?",
        question_type: "option",
        choice: [
          {
            id: 28,
            text: "A significant impact — I need this investment for a specific purpose.",
          },
          {
            id: 29,
            text: "Some impact — I would have to adjust my financial plans.",
          },
          { id: 30, text: "No impact — I have other savings to fall back on." },
        ],
      },
    ],
    []
  );
  const [questions, setQuestions] = useState<Question[]>([]);
  const [responses, setResponses] = useState<{ [key: number]: UserResponse }>(
    {}
  );
  const [showContactModal, setShowContactModal] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error] = useState<string | null>(null);
  const [showSustainabilityScreen, setShowSustainabilityScreen] =
    useState(true);
  const [sustainabilityPreference, setSustainabilityPreference] = useState<
    "yes" | "no" | null
  >(null);
  const [sustainabilityError, setSustainabilityError] = useState<string | null>(
    null
  );
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [results, setResults] = useState<RiskProfileResult | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const [age, setAge] = useState<string>("18");
  const textQuestions = questions.filter((q) => q.question_type === "text");
  const optionQuestions = questions.filter((q) => q.question_type !== "text");
  const [hasSubmittedModal, setHasSubmittedModal] = useState(false);
  const questionRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const token = window._env_?.BASE_TOKEN;

  useEffect(() => {
    const initialResponses: { [key: number]: UserResponse } = {};
    questionsData.forEach((question) => {
      initialResponses[question.id] = {
        question: question.id,
        selected_choices: [],
        text_response: question.id === 1 ? "1" : "",
      };
      questionRefs.current[question.id] = null;
    });
    setQuestions(questionsData);
    setResponses(initialResponses);
    setLoading(false);
  }, []);

  const handleResponseChange = useCallback(
    (questionId: number, value: number) => {
      setResponses((prevResponses) => {
        const currentSelection =
          prevResponses[questionId]?.selected_choices[0]?.id;
        return {
          ...prevResponses,
          [questionId]: {
            ...prevResponses[questionId],
            selected_choices: currentSelection === value ? [] : [{ id: value }],
          },
        };
      });
      setGlobalError(null);
    },
    []
  );

  const handleTextChange = useCallback((questionId: number, value: string) => {
    setResponses((prevResponses) => ({
      ...prevResponses,
      [questionId]: {
        ...prevResponses[questionId],
        text_response: value,
        selected_choices: [],
      },
    }));
    setGlobalError(null);
  }, []);

  const handleCalculate = async () => {
    try {
      setLoading(true);
      setGlobalError(null);
      setResults(null);
      const unansweredQuestions = questions.filter((question) => {
        const response = responses[question.id];
        if (question.id === 1) return false;
        if (question.question_type === "text") {
          return !response?.text_response?.trim();
        } else {
          return response?.selected_choices?.length === 0;
        }
      });

      if (unansweredQuestions.length > 0) {
        const firstUnansweredId = unansweredQuestions[0].id;
        const refToScroll = questionRefs.current[firstUnansweredId];

        if (refToScroll) {
          refToScroll.scrollIntoView({ behavior: "smooth", block: "center" });
        }

        setGlobalError(
          "Please ensure all questions are answered before proceeding."
        );
        setLoading(false);
        return;
      }

      if (!token) {
        throw new Error("Authentication token not found");
      }

      const questionsAnswers = questions.map((question) => {
        const response = responses[question.id];
        let answer = "";

        if (question.question_type === "text") {
          answer = response.text_response?.trim() || "";
        } else {
          const selectedChoiceId = response.selected_choices[0]?.id;
          const selectedChoice = question.choice.find(
            (c) => c.id === selectedChoiceId
          );
          answer = selectedChoice?.text?.trim() || "";
        }

        return {
          question: question.question,
          answer: answer,
        };
      });
      const invalidAnswers = questionsAnswers.filter(
        (qa) => !qa.answer || qa.answer.trim() === ""
      );

      if (invalidAnswers.length > 0) {
        setGlobalError(
          "Some answers were missing or invalid. Please try again."
        );
        setLoading(false);
        return;
      }
      questionsAnswers.unshift({
        question: "What age are you?",
        answer: age,
      });

      const payload = {
        questions_answers: questionsAnswers,
      };

      const endpoint = `${apiUrl}/tools/risk-profile/`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.detail ||
            errorData.message ||
            "Failed to calculate risk profiler"
        );
      }

      const result = await response.json();
      setResults(result);
      setShowContactModal(true);
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (err: any) {
      console.error("Error calculating risk profiler:", err);
      setGlobalError(err.message || "Failed to calculate risk profiler");
    } finally {
      setLoading(false);
    }
  };

  const handleFormSuccess = useCallback(() => {
    setShowContactModal(false);
    setFormSubmitted(true);
    setHasSubmittedModal(true);
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  }, []);

  const handleClearAll = () => {
    const clearedResponses: { [key: number]: UserResponse } = {};
    questions.forEach((q) => {
      clearedResponses[q.id] = {
        ...responses[q.id],
        selected_choices: [],
        text_response: q.id === 1 ? "1" : "",
      };
    });
    setResponses(clearedResponses);
    setAge("18");
    setGlobalError(null);
    setResults(null);
    setFormSubmitted(false);
    setHasSubmittedModal(false);
    setShowContactModal(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const riskProfileDescriptions: Record<number, string> = useMemo(
    () => ({
      1: `Your answers suggest that your risk appetite is Risk Profile 1. This means you prefer capital stability over growth and are very cautious about risk. You are unlikely to tolerate fluctuations in the value of your investment, even in the short term.
Funds with a Risk Profile of 1 are typically invested in lower-risk assets such as cash or high-quality short-term bonds. These investments aim to preserve capital, but they may also offer limited potential for returns, especially after inflation.`,
      2: `Your answers suggest that your risk appetite is Risk Profile 2. You are willing to accept a small amount of risk in exchange for modest potential returns. You prefer stability but are open to minor fluctuations in value.
Funds with a Risk Profile of 2 generally focus on lower-risk investments such as government bonds or investment-grade fixed income. While they offer some growth potential, the emphasis remains on capital preservation and reduced volatility.`,
      3: `Your answers suggest that your risk appetite is Risk Profile 3. You are relatively cautious but understand that accepting a moderate amount of risk may help your investments grow over time.
Funds with a Risk Profile of 3 may include a balanced mix of fixed income and limited exposure to equities. These portfolios aim to provide steady growth with some level of protection against market downturns.`,
      4: `Your answers suggest that your risk appetite is Risk Profile 4. You are comfortable with a moderate level of risk and accept that your investments may rise and fall in value in pursuit of better long-term returns.
Funds with a Risk Profile of 4 typically invest in a diversified mix of asset classes including equities, bonds, and other instruments. They seek a balance between risk and reward.`,
      5: `Your answers suggest that your risk appetite is Risk Profile 5. You are willing to take on above-average risk in exchange for the potential of higher returns. You understand and accept that your investment value may experience noticeable fluctuations.
Funds with a Risk Profile of 5 tend to have greater exposure to growth assets such as equities, possibly alongside some alternative investments. These may offer stronger long-term returns but with increased short-term volatility.`,
      6: `Your answers suggest that your risk appetite is Risk Profile 6. This means that you aim for high possible returns and accept higher levels of risk, recognising the value of your investment may fluctuate very sharply, particularly over the short term.
Funds with a Risk Profile of 6 are likely to invest in high-risk assets, such as equities or commodities. The assets may see large day-to-day changes in value, both negative and positive.`,
      7: `Your answers suggest that your risk appetite is Risk Profile 7. You are highly risk-tolerant and seek the maximum potential for return, fully accepting the possibility of significant and frequent fluctuations in your investment value.
Funds with a Risk Profile of 7 are heavily weighted toward high-volatility assets such as emerging market equities, tech stocks, or speculative investments. These carry a greater risk of loss but also the potential for substantial long-term gains.`,
    }),
    []
  );

  const pageTitle = "Risk Profiler | Apollo Financial Services";
  const pageDescription =
    "Use our risk profiler tool to assess your investment risk tolerance and get personalized recommendations for your financial strategy.";
  const canonicalUrl = "https://www.apollofinancial.ie/risk-profiler";
  const pageKeywords =
    "risk profiler, investment risk assessment, financial planning tool, risk tolerance calculator, investment strategy, financial risk analysis";

  if (error) return <div>Error: {error}</div>;
  const isNextDisabled = sustainabilityPreference !== "yes";

  if (showSustainabilityScreen) {
    return (
      <div className="risk-container">
        <section className="tools-hero">
          <div className="hero-overlay">
            <div>
              <h1>Apollo Financial Risk Profiler</h1>
              <p>
                Understand your attitude to risk and make better-informed
                investment decisions.
              </p>
            </div>
          </div>
        </section>
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
          <meta name="keywords" content={pageKeywords} />
          <link rel="canonical" href={canonicalUrl} />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDescription} />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:type" content="website" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={pageTitle} />
          <meta name="twitter:description" content={pageDescription} />
        </Helmet>
        <div className="sustainability-screen">
          <h3>Disclaimer: Apollo Financial Risk Profiler</h3>
          <p style={{ textAlign: "left", margin: "20px" }}>
            The Apollo Financial Risk Profiler is an illustrative tool intended
            to help you better understand your personal attitude toward
            investment risk. This tool is designed for informational purposes
            only and does not constitute financial advice from Apollo Financial.
            Before making any investment decisions, it is essential that you
            assess your overall financial situation and goals in consultation
            with a Qualified Financial Advisor(QFA).
          </p>
          <p style={{ textAlign: "left", margin: "20px" }}>
            Please note: The Risk Profiler may not consider all relevant factors
            that influence your risk profile. Professional financial advice is
            strongly recommended prior to acting on any information derived from
            this tool. By clicking "Accept", you confirm that: You have not
            received financial advice from Apollo Financial in relation to this
            tool; You understand that the Apollo Financial Risk Profiler is not
            a substitute for personalised advice from a Qualified Financial
            Advisor (QFA).
          </p>

          <div className="sustainability-options">
            <label>
              <input
                type="radio"
                name="sustainability"
                value="yes"
                checked={sustainabilityPreference === "yes"}
                onChange={() => {
                  setSustainabilityPreference("yes");
                  setSustainabilityError(null);
                }}
              />
              I have read and accept the disclaimer.
            </label>
          </div>
          {sustainabilityError && (
            <div className="error-container">
              <FontAwesomeIcon
                icon={faExclamationCircle}
                className="error-icon"
              />
              <span>{sustainabilityError}</span>
            </div>
          )}

          <button
            className="retirement-calculate-btn"
            style={{ marginTop: "1.5rem", marginBottom: "1.5rem" }}
            onClick={() => {
              if (!sustainabilityPreference) {
                setSustainabilityError(
                  "Please accept the disclaimer to continue."
                );
              } else if (sustainabilityPreference === "no") {
                setSustainabilityError(
                  "Please accept compliance question before moving on"
                );
              } else {
                window.scrollTo({ top: 0, behavior: "smooth" });
                setSustainabilityError(null);
                setShowSustainabilityScreen(false);
              }
            }}
            disabled={isNextDisabled}
          >
            Next
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="risk-container">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Risk Profiler",
            url: "https://www.apollofinancial.ie/resources/risk-profiler/",
            description:
              "Use our risk profiler tool to assess your investment risk tolerance and get personalized recommendations for your financial strategy.",
            publisher: {
              "@type": "Organization",
              name: "Apollo Financial Services",
              url: "https://www.apollofinancial.ie",
              logo: {
                "@type": "ImageObject",
                url: "https://www.apollofinancial.ie/assets/logo.png",
              },
            },
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://www.apollofinancial.ie/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Resources",
                item: "https://www.apollofinancial.ie/resources/",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: "Risk Profiler",
                item: "https://www.apollofinancial.ie/resources/risk-profiler/",
              },
            ],
          })}
        </script>
      </Helmet>
      <section className="tools-hero">
        <div className="hero-overlay">
          <div>
            <h1>Apollo Financial Risk Profiler</h1>
            <p>
              Understand your attitude to risk and make better-informed
              investment decisions.
            </p>
          </div>
        </div>
      </section>
      <form className="risk-form">
        <div className="question">
          <label>
            1. What age are you? <span className="asterisk">*</span>
          </label>
          <div
            className="text-input-container"
            style={{ gap: "10px", alignItems: "center" }}
          >
            <input
              type="range"
              min={18}
              max={100}
              value={age || "18"}
              onChange={(e) => setAge(e.target.value)}
              className="text-input"
              style={{ flex: 1 }}
            />
            <button
              type="button"
              className="decrement-button"
              onClick={() =>
                setAge((prev) => String(Math.max(18, Number(prev) - 1)))
              }
            >
              −
            </button>
            <button
              type="button"
              className="increment-button"
              onClick={() =>
                setAge((prev) => String(Math.min(100, Number(prev) + 1)))
              }
            >
              +
            </button>
            <span>{parseInt(age || "18", 10)} years</span>
          </div>
        </div>
        <div className="question">
          <label>
            2. How long do you plan to invest for?{" "}
            <span className="asterisk">*</span>
          </label>
          <div
            className="text-input-container"
            style={{ gap: "10px", alignItems: "center" }}
          >
            <input
              type="range"
              min={1}
              max={15}
              value={responses[1]?.text_response || "1"}
              onChange={(e) => handleTextChange(1, e.target.value)}
              className="text-input"
              style={{ flex: 1 }}
            />
            <button
              type="button"
              className="decrement-button"
              onClick={() =>
                handleTextChange(
                  1,
                  String(
                    Math.max(1, Number(responses[1]?.text_response || "1") - 1)
                  )
                )
              }
            >
              −
            </button>
            <button
              type="button"
              className="increment-button"
              onClick={() =>
                handleTextChange(
                  1,
                  String(
                    Math.min(50, Number(responses[1]?.text_response || "1") + 1)
                  )
                )
              }
            >
              +
            </button>
            <span>
              {responses[1]?.text_response || "1"}{" "}
              {Number(responses[1]?.text_response || "1") === 1
                ? "year"
                : "years"}
            </span>
          </div>
        </div>

        {textQuestions
          .filter((question) => question.id !== 1)
          .map((question, index) => {
            const textResponse = responses[question.id]?.text_response;
            const numericValue = textResponse
              ? parseInt(textResponse, 10)
              : NaN;
            const displayText = isNaN(numericValue)
              ? "years"
              : numericValue === 1
              ? "year"
              : "years";

            return (
              <div
                key={question.id}
                className={`question ${
                  globalError && !responses[question.id]?.text_response?.trim()
                    ? "error-border"
                    : ""
                }`}
                ref={(el) => {
                  questionRefs.current[question.id] = el;
                }}
              >
                <label>
                  {index + 2}. {question.question}{" "}
                  <span className="asterisk">*</span>
                </label>
                <div className="text-input-container">
                  <input
                    type="number"
                    value={textResponse || ""}
                    onChange={(e) =>
                      handleTextChange(question.id, e.target.value)
                    }
                    className={`text-input ${
                      globalError && !textResponse ? "error" : ""
                    }`}
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder="Type your answer here..."
                  />
                  <span>{displayText}</span>
                </div>
              </div>
            );
          })}
        {optionQuestions.map((question, index) => (
          <div
            key={question.id}
            className={`question ${
              globalError &&
              (!responses[question.id]?.selected_choices ||
                responses[question.id]?.selected_choices.length === 0)
                ? "error-border"
                : ""
            }`}
            ref={(el) => {
              questionRefs.current[question.id] = el;
            }}
          >
            <label>
              {textQuestions.length + index + 2}. {question.question}{" "}
              <span className="asterisk">*</span>
            </label>
            <div className="options">
              {question.choice?.map((choice) => (
                <label key={choice.id} className="option-label">
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    value={choice.id}
                    checked={
                      responses[question.id]?.selected_choices.some(
                        (s) => s.id === choice.id
                      ) || false
                    }
                    onChange={() =>
                      handleResponseChange(question.id, choice.id)
                    }
                  />
                  {choice.text}
                </label>
              ))}
            </div>
          </div>
        ))}
        {globalError && (
          <div className="error-container">
            <div className="global-error">
              <FontAwesomeIcon
                icon={faExclamationCircle}
                className="error-icon"
              />
              <span>{globalError}</span>
            </div>
          </div>
        )}
        <div className="retirement-calculator-actions">
          <button
            type="button"
            className="retirement-back-btn"
            onClick={() => {
              setShowSustainabilityScreen(true);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            Back
          </button>

          {!(hasSubmittedModal && results) && (
            <button
              type="button"
              className="retirement-calculate-btn"
              onClick={handleCalculate}
              disabled={loading}
            >
              {loading ? "Showing Risk Profile..." : "Show Risk Profile"}
            </button>
          )}
          <button
            type="button"
            className="retirement-clear-btn"
            onClick={handleClearAll}
          >
            Clear
          </button>
        </div>
      </form>

      <AnimatePresence>
        {formSubmitted && results && (
          <div className="risk-profile-results">
            <h3 ref={resultsRef} style={{ scrollMarginTop: "100px" }}>
              Risk Profiler Results
            </h3>
            <p className="result-description">
              Based on your answers to the questionnaire
            </p>

            <div className="results-grid">
              <div className="result-item">
                <label>Risk Profile</label>
                <div className={`result-value risk-profile-value`}>
                  {results.risk_label}
                </div>
              </div>

              <div className="result-item">
                <label>Risk Level</label>
                <div
                  className={`risk-scale-item risk-level-${results.risk_level} active`}
                  style={{ margin: "0 auto" }}
                >
                  {results.risk_level}
                </div>
              </div>
            </div>
            <div className="risk-scale-container">
              <label
                style={{ fontWeight: 600, color: "#000000", display: "block" }}
              >
                Risk Scale
              </label>
              <div className="risk-scale">
                <div className="risk-scale-labels">
                  <span>Lower</span>
                  <span>Higher</span>
                </div>
                <div className="risk-scale-items">
                  {[1, 2, 3, 4, 5, 6, 7].map((level) => (
                    <div
                      key={level}
                      className={`risk-scale-item risk-level-${level} ${
                        results.risk_level === level ? "active" : ""
                      }`}
                    >
                      {level}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="results-vertical-stack">
              <div className="result-item">
                {results.risk_level && (
                  <div
                    className="risk-profile-description"
                    style={{ marginTop: "2rem" }}
                  >
                    <label>About your Risk Profile</label>
                    <span style={{ textAlign: "left", display: "block" }}>
                      {riskProfileDescriptions[results.risk_level]}
                    </span>
                  </div>
                )}
              </div>
              <div className="result-item">
                <div
                  className="risk-profile-description"
                  style={{ marginTop: "2rem" }}
                >
                  <label>Disclaimer</label>
                  <span
                    style={{
                      textAlign: "left",
                      fontStyle: "italic",
                      display: "block",
                    }}
                  >
                    The Apollo Financial Risk Profiler is an illustrative tool
                    intended to help you better understand your personal
                    attitude toward investment risk. This tool is designed for
                    informational purposes only and does not constitute
                    financial advice from Apollo Financial. Before making any
                    investment decisions, it is essential that you assess your
                    overall financial situation and goals in consultation with a
                    Qualified Financial Advisor(QFA). Please note: The Risk
                    Profiler may not consider all relevant factors that
                    influence your risk profile. Professional financial advice
                    is strongly recommended prior to acting on any information
                    derived from this tool.
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>
      {showContactModal && (
        <div className="download-modal-overlay">
          <div className="contact-modal">
            <button
              className="modal-close-btn"
              onClick={() => setShowContactModal(false)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <h3>Access Your Investment Risk Profile Results</h3>
            <p>
              Fill in the form to access your results. Based on your profile,
              one of our advisors will follow up shortly with personalised
              guidance tailored to your needs.
            </p>
            <div className="modal-contact-form">
              <ContactForm
                onSuccess={handleFormSuccess}
                onError={(error) => console.error("Form error:", error)}
                context="risk-profiler"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tools;
